"use client"

import { useEffect, useRef } from "react"
import { useFormContext } from "react-hook-form"
import { animateChildren } from "@/lib/animation-utils"
import { Clock, FileText, Calendar, Skull } from "lucide-react"

import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"

export default function CalfDevelopmentStep() {
  const { control, formState: { dirtyFields } } = useFormContext()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      // Set initial state for children
      Array.from(containerRef.current.children).forEach((child) => {
        ;(child as HTMLElement).style.opacity = "0"
        ;(child as HTMLElement).style.transform = "translateY(20px)"
      })

      // Animate children with staggered delay
      requestAnimationFrame(() => {
        if (containerRef.current) {
          animateChildren(
            containerRef.current,
            {
              opacity: 1,
              translateY: 0,
            },
            {
              duration: 400,
              easing: "ease-out",
            },
          )
        }
      })
    }
  }, [])

  return (
    <div ref={containerRef} className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-slate-900">Calf Development</h2>
        <p className="text-slate-500">Enter information about the calf's development and status</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormField
          control={control}
          name="gestationPeriod"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Clock className="h-4 w-4" />
                Gestation Period (Days)
                {dirtyFields.gestationPeriod && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="Enter number of days" {...field} />
              </FormControl>
              {dirtyFields.gestationPeriod && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="calfBirthNotes"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <FileText className="h-4 w-4" />
                Birth Notes
                {dirtyFields.calfBirthNotes && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter any notes about the birth"
                  className="min-h-[100px] resize-none"
                  {...field}
                />
              </FormControl>
              {dirtyFields.calfBirthNotes && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="md:col-span-2">
          <div className="mb-4 border-b pb-2">
            <h3 className="text-lg font-medium text-slate-900">Mortality Information (if applicable)</h3>
          </div>
        </div>

        <FormField
          control={control}
          name="calfDeathDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Calendar className="h-4 w-4" />
                Death Date (if applicable)
                {dirtyFields.calfDeathDate && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              {dirtyFields.calfDeathDate && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="calfDeathNotes"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <Skull className="h-4 w-4" />
                Death Notes (if applicable)
                {dirtyFields.calfDeathNotes && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter any notes about the cause of death"
                  className="min-h-[100px] resize-none"
                  {...field}
                />
              </FormControl>
              {dirtyFields.calfDeathNotes && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}
